import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { analyticsApi, ApiError } from "@/services/apiClient";
import { useFilterStore, isReadOnlyEnv } from "@/stores/filterStore";

// 모바일 계정 Role 변경 sheet (P3, 2026-09-17).
// desktop AccountRoleSection 과 동일 role enum(admin/test/none) + 동일 API(adminSetAccountRole).
// email 기준이라 email 있는 사용자만 호출부(MobileUserList)에서 버튼 노출.
// 현재 role 은 desktop 도 GET 으로 미조회 → "확인 불가"로 표시(원칙 C), 변경 후 res.role 로 after.
const ROLES = [
  { id: "admin", label: "관리자", desc: "백오피스 접근 + 지표 제외" },
  { id: "test", label: "테스트", desc: "지표 집계 제외" },
  { id: "none", label: "일반 고객", desc: "모든 집계 포함 (기본)" },
];

export default function MobileRoleChangeSheet({ user, onClose, onSuccess }) {
  const readOnly = isReadOnlyEnv(useFilterStore((s) => s.env));
  const [selected, setSelected] = useState("none");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [resultRole, setResultRole] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose?.(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose, submitting]);

  const email = (user.email || "").trim().toLowerCase();
  const targetLabel = ROLES.find((r) => r.id === selected)?.label || selected;

  async function submit() {
    if (readOnly) { setError("STG 조회 상태에서는 역할 변경이 비활성화됩니다."); return; }
    if (!email) { setError("이메일이 없는 계정은 역할을 변경할 수 없습니다."); return; }
    if (!confirming) { setConfirming(true); setError(null); return; }
    setSubmitting(true);
    setError(null);
    try {
      const res = await analyticsApi.adminSetAccountRole({ email, role: selected });
      setResultRole(res.role || selected);
      onSuccess?.({ email, role: res.role || selected });
    } catch (err) {
      if (err.status === 403 || err.code === "FORBIDDEN") setError("권한 없음 — STG 는 Role 쓰기가 제한될 수 있습니다.");
      else setError(err instanceof ApiError ? err.message : String(err));
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={() => !submitting && onClose?.()} aria-hidden="true" />
      <div className="relative w-full rounded-t-2xl bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-[15px] font-bold text-ink-900">계정 Role 변경</h3>
          <button type="button" onClick={() => !submitting && onClose?.()} aria-label="닫기"
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-ink-500 hover:bg-ink-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="truncate text-[13px] text-ink-500">{email || "(이메일 없음)"}</div>

        {resultRole ? (
          <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-3 text-[13px] font-medium text-emerald-800">
            변경 완료 — 현재 Role: {ROLES.find((r) => r.id === resultRole)?.label || resultRole}
          </div>
        ) : readOnly ? (
          <div className="mt-3 rounded-xl bg-ink-100 px-3.5 py-3 text-[13px] font-medium text-ink-500">
            STG 조회 전용 — 역할 변경이 비활성화되어 있습니다.
          </div>
        ) : (
          <>
            {/* before: 현재 role 은 조회 API 가 없어 확인 불가 */}
            <div className="mt-3 rounded-xl border border-ink-200 bg-ink-50/60 px-3.5 py-2.5 text-[12px] text-ink-500">
              현재 Role: 확인 불가 (조회 API 없음) → 변경: <span className="font-semibold text-ink-800">{targetLabel}</span>
            </div>

            <div className="mt-3 space-y-1.5">
              {ROLES.map((r) => (
                <button key={r.id} type="button" onClick={() => { setSelected(r.id); setConfirming(false); }}
                  className={`flex w-full items-start gap-2 rounded-xl border px-3.5 py-3 text-left ${selected === r.id ? "border-brand-500 bg-brand-50" : "border-ink-200 bg-white"}`}>
                  <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${selected === r.id ? "border-brand-500" : "border-ink-300"}`}>
                    {selected === r.id && <span className="h-2 w-2 rounded-full bg-brand-500" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[14px] font-semibold text-ink-800">{r.label}</span>
                    <span className="block text-[11px] text-ink-400">{r.desc}</span>
                  </span>
                </button>
              ))}
            </div>

            {error && <div className="mt-2 text-[12px] text-red-600">{error}</div>}

            <div className="mt-3 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => !submitting && onClose?.()} disabled={submitting}
                className="h-12 rounded-xl border border-ink-200 bg-white text-[14px] font-semibold text-ink-600 disabled:opacity-50">취소</button>
              <button type="button" onClick={submit} disabled={submitting || !email}
                className="h-12 rounded-xl bg-brand-600 text-[14px] font-semibold text-white disabled:opacity-50">
                {submitting ? "처리 중…" : confirming ? `정말 ${targetLabel}로 변경` : "변경 확인"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
